import { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { ViewToolbar } from '@/components/layout/ViewToolbar';
import { PlanningBoard } from '@/components/planning/PlanningBoard';
import { BottomBar } from '@/components/layout/BottomBar';
import { NavMenu } from '@/components/layout/NavMenu';
import { KanbanPage } from '@/pages/KanbanPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { PersonalTasksPage } from '@/pages/PersonalTasksPage';
import { FeedPage } from '@/pages/FeedPage';
import { PlannerView } from '@/components/planner/PlannerView';
import { CalendarView } from '@/components/calendar/CalendarView';
import { TableView } from '@/components/table/TableView';
import { TaskModal } from '@/components/task-modal/TaskModal';
import { FilterPanel } from '@/components/filters/FilterPanel';
import { SitePlan } from '@/components/site-plan/SitePlan';
import { ConfiguracionPage } from '@/pages/ConfiguracionPage';
import { LotesKanbanPage } from '@/pages/LotesKanbanPage';
import { BootstrapPage } from '@/pages/BootstrapPage';
import { PlantaPage } from '@/pages/PlantaPage';
// Exploración de láminas, aislada de /planta (ver pages/LaminasPage.tsx).
import { LaminasPage } from '@/pages/LaminasPage';
// Inventario de formaleta: nace separable porque se va a independizar de
// ArquiFlow (ver pages/FormaletaPage.tsx). Si se muda, se borra esta línea y
// la ruta de abajo.
import { FormaletaPage } from '@/pages/FormaletaPage';
import { DiagnosticoPage } from '@/pages/DiagnosticoPage';
import { useAppStore } from '@/store/appStore';

// Gantt: lazy-loaded porque @svar-ui/react-gantt es pesado (convención del
// repo para deps grandes en vez de meterlas en un vendor chunk).
const GanttPage = lazy(() => import('@/pages/GanttPage'));

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30 * 1000, retry: 1 } }
});

// Maneja deep links de push notifications: ?task=<id> abre el TaskModal
// inmediatamente y limpia el query param para que un refresh no lo reabra.
// Vive dentro de BrowserRouter para poder usar useSearchParams.
function DeepLinkHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectTask = useAppStore(s => s.selectTask);

  useEffect(() => {
    const raw = searchParams.get('task');
    if (!raw) return;
    const id = Number.parseInt(raw, 10);
    if (Number.isFinite(id) && id > 0) {
      selectTask(id);
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, selectTask]);

  return null;
}

// Contenido principal: en modo planificación el PlanningBoard toma toda el
// área de trabajo; si no, las rutas normales.
function MainContent() {
  const planningMode = useAppStore((s) => s.planningMode);
  if (planningMode) return <PlanningBoard />;
  return (
    <Suspense fallback={<div className="p-8 text-sm text-stone-400">Cargando…</div>}>
    <Routes>
      <Route path="/" element={<DashboardPage />} />
      <Route path="/gantt" element={<GanttPage />} />
      <Route path="/personal/:idCol" element={<PersonalTasksPage />} />
      <Route path="/feed" element={<FeedPage />} />
      <Route path="/kanban/:groupBy" element={<KanbanPage />} />
      <Route path="/planner" element={<PlannerView />} />
      <Route path="/calendar" element={<CalendarView />} />
      <Route path="/table" element={<TableView />} />
      <Route path="/site-plan" element={<SitePlan />} />
      <Route path="/planta" element={<PlantaPage />} />
      <Route path="/laminas" element={<LaminasPage />} />
      <Route path="/formaleta" element={<FormaletaPage />} />
      <Route path="/tramites" element={<LotesKanbanPage />} />
      {/* Bandeja de bootstrap CFIA (B3): andamio transitorio para vincular
          codigo->lote. Entra desde Tramites, NO desde el nav top-level. */}
      <Route path="/tramites/bootstrap" element={<BootstrapPage />} />
      {/* Plantillas vive ahora como tab dentro de Configuracion.
          Mantenemos un redirect para que bookmarks viejos sigan vivos. */}
      <Route path="/plantillas" element={<Navigate to="/configuracion" replace />} />
      <Route path="/configuracion" element={<ConfiguracionPage />} />
      {/* Temporal: los números que el aparato reporta de su propia pantalla.
          Se quita cuando se cierre el hueco de abajo del 29-oct-2026. */}
      <Route path="/diagnostico" element={<DiagnosticoPage />} />
    </Routes>
    </Suspense>
  );
}

// Shell: TopBar (h-12) + main flexible + BottomBar (h-16). NavMenu y
// FilterPanel viven montados siempre como drawers overlay (izq y der).
//
// En `pantallaCompleta` las dos franjas no se pintan y `main` se queda con los
// 112 px que le robaban. La vista que la enciende se compromete a dibujar su
// propia salida —el editor de planta tiene la X en su cabecera—; sin eso, matar
// la BottomBar deja la pantalla sin navegación y sin forma de volver.
export function Shell() {
  const pantallaCompleta = useAppStore((s) => s.pantallaCompleta);
  // `h-full` y no `dvh`: el alto lo da `#root`, que está clavado a los cuatro
  // bordes de la pantalla con `position:fixed; inset:0`. En iOS standalone
  // `dvh` descuenta la franja de la hora y el shell quedaba 62 px corto.
  // El porqué, medido, está en el comentario de `styles/index.css`.
  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {!pantallaCompleta && <TopBar />}
      <ViewToolbar />
      <main className="relative flex-1 overflow-hidden">
        <MainContent />
      </main>
      {!pantallaCompleta && <BottomBar />}
      <NavMenu />
      <FilterPanel />
      <TaskModal />
      <DeepLinkHandler />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </QueryClientProvider>
  );
}